import React, { useEffect, useMemo, useState } from "react";
import SelectTemplateOption from "../../../../../leads/lead-view/lead-view-details/lead-view-details-view/lead-view-details-view-email-info/select-template-option-dropdown";
import { emailComposeOptions } from "../../../../../leads/lead-filter/lead-filter.constant";
import { useLogin } from "../../../../../../common/hooks/useUser";
import "./campaign-email-info.scss";
import DataTable from "../campaign-view-details-view-activities-info/task-table/task-table";
import TemplateListPopup from "../../../../../../common/components/templates-list-popup/template-list-popup";
import {
  AlertComponent,
  ConfirmationPopup,
} from "../../../../../../common/components";
import { useSetLoading } from "../../../../../../common/hooks/appearance";
import { getEmailLetterTemplates, getTemplateHeaderOrFooter } from "../../../../../../api/email-letter-templates";
import {
  deleteEmail,
  getCampaignEmails,
  getEachCampaignEmailDetails,
} from "../../../../../../api/campaign";
import ComposeEmailDialog from "./compose-email";
import { formatDateTime } from "../../../../../../utils/utils";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Tooltip,
} from "@mui/material";
import { Close } from "@mui/icons-material";
import DeleteIcon from "../../../../../../assets/icons/archive-icon.svg";
import EmailSetUpBanner from "../../../../../../common/components/email-setup-required-banner/email-setup-banner";
interface CampaignEmailInfoProps {
  campaignDetails?: any;
  setLoading: (loading: boolean) => void;
  setAlertMessage: (message: string) => void;
  setAlertType: (type: "success" | "error") => void;
  setAlertOpen: (open: boolean) => void;
  permissions?: any;
  fetchCampaignDetails?: () => Promise<void> | void;
}

const CampaignEmailInfo: React.FC<CampaignEmailInfoProps> = ({
  campaignDetails,
  setLoading,
  setAlertMessage,
  setAlertType,
  setAlertOpen,
  permissions,
  fetchCampaignDetails,
}) => {
  const [selectedOption, setSelectedOption] = useState("");
  const [emailsData, setEmailsData] = useState([]);
  const [isComposeOpen, setIsComposeOpen] = useState(false);
  const [isTemplateListOpen, setIsTemplateListOpen] = useState(false);
  const [templates, setTemplates] = useState<any[]>([]);
  const [selectedTemplateData, setSelectedTemplateData] = useState<any>(null);
  const [templateMetaData, setTemplateMetaData] = useState<any>(null);
  const [isRecipientsOpen, setIsRecipientsOpen] = useState(false);
  const [recipientData, setRecipientData] = useState<any[]>([]);
  const [emailToDelete, setEmailToDelete] = useState<any>(null);
  const [isArchivePopupOpen, setIsArchivePopupOpen] = useState(false);

  const loginDetails = useLogin();
  const isEmailConfigured = loginDetails?.isEmailConfigured;

  const campaignEmailColumns = [
    { label: "Subject", key: "subject", render: (row: any) => row.subject },
    {
      label: "Owner",
      key: "owner",
      render: (row: any) =>
        [row?.createdBy?.first_name, row?.createdBy?.last_name]
          .filter(Boolean)
          .join(" "),
    },
    {
      label: "Recipients",
      key: "recipients",
      render: (row: any) => (
        <span
          style={{
            cursor: row.campaignEmailCount > 0 ? "pointer" : "default",
            color: "#1194DA",
            textDecoration: row.campaignEmailCount === 0 ? "none" : "underline",
          }}
          onClick={() => {
            if (row.campaignEmailCount === 0) return;
            setIsRecipientsOpen(true);
            fetchRecipientDetails(row.id);
          }}
        >
          {row.campaignEmailCount}
        </span>
      ),
    },
    {
      label: "Date & time",
      key: "dateTime",
      render: (row: any) => formatDateTime(row.created_at),
    },
    {
      label: "Status",
      key: "status",
      render: (row: any) => (row.status ? "Sent" : "Failed"),
    },
  ];

  const recipentColumns = [
    { label: "Name", key: "name", render: (row: any) => row.name },
    { label: "Email", key: "email", render: (row: any) => row.email },
    {
      label: "Delivery Status",
      key: "deliveryStatus",
      render: (row: any) => row.deliveryStatus,
    },
    { label: "Time", key: "time", render: (row: any) => row.time },
  ];

  const handleOptionChange = async (e: any) => {
    setSelectedOption(e.target.value);
    // Refresh campaign details to ensure recipients are up-to-date
    if (fetchCampaignDetails) {
      try {
        setLoading(true);
        await fetchCampaignDetails();
      } finally {
        setLoading(false);
      }
    }
    if (e.target.value === "Compose New Mail") {
      setIsComposeOpen(true);
    }
    if (e.target.value === "Select from Templates") {
      // Open the template list popup
      setIsComposeOpen(false);
      setIsTemplateListOpen(true);
    }
  };

  const handleTemplateSelect = () => {
    setIsComposeOpen(true);
    setIsTemplateListOpen(false);
  };

  const fetchEmailTemplates = async (
    search: string = "",
    filters: any = {},
  ) => {
    setLoading(true);
    try {
      const params: any = {
        fetchAll: true,
        templateType: "Email",
      };
      if (filters?.folderId) {
        if (filters.folderId !== "all" && filters.folderId !== "notInFolder") {
          params.folderId = Number(filters.folderId);
        }
        if (filters.folderId === "notInFolder") {
          params.notInFolder = true;
        }
      }
      if (search) {
        params.searchText = search;
      }
      const response = await getEmailLetterTemplates(params);
      setTemplates(response.data.data.result);
      setTimeout(() => setLoading(false), 1000);
    } catch (error: any) {
      console.error("Error fetching email templates:", error);
      setAlertMessage(
        error?.response?.data?.message || "Failed to fetch email templates.",
      );
      setAlertType("error");
      setAlertOpen(true);
      setLoading(false);
    }
  };

  const fetchEmails = async () => {
    setLoading(true);
    try {
      const response = await getCampaignEmails({
        campaignId: campaignDetails?.id,
      });
      setEmailsData(response.data.data.data);
    } catch (error) {
      console.error("Error fetching emails:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchRecipientDetails = async (campaignEmailId: number) => {
    setLoading(true);
    try {
      const response = await getEachCampaignEmailDetails(campaignEmailId);
      const formattedResponse = response?.data?.data?.data.map((item: any) => {
        return {
          name:
            [
              item?.lead?.first_name,
              item?.lead?.middle_name,
              item?.lead?.last_name,
            ]
              .filter(Boolean)
              .join(" ") ||
            [
              item?.customer?.first_name,
              item?.customer?.middle_name,
              item?.customer?.last_name,
            ]
              .filter(Boolean)
              .join(" ") ||
            "-",
          email:
            item?.lead?.primary_email_address ||
            item?.customer?.primary_email_address ||
            item?.email_address,
          deliveryStatus: item.email_status,
          time: formatDateTime(item.created_at),
        };
      });
      setRecipientData(formattedResponse);
    } catch (error) {
      console.error("Error fetching recipient details:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTemplateHeaderFooter = async () => {
    try {
      const headerResponse = await getTemplateHeaderOrFooter();
      if (headerResponse?.data?.data)
        setTemplateMetaData(headerResponse.data.data);
    } catch (error: any) {
      console.error(
        "Error fetching template header, footer:",
        error?.response?.data?.message,
      );
      setAlertMessage(
        error?.response?.data?.message ||
          "Failed to fetch template header, footer.",
      );
      setAlertType("error");
      setAlertOpen(true);
    }
  };

  useEffect(() => {
    fetchEmails();
    fetchEmailTemplates();
    fetchTemplateHeaderFooter();
  }, []);

  const handleEmailSuccess = () => {
    setAlertMessage("Email Sent Succeessfully.");
    setAlertType("success");
    setAlertOpen(true);
    setSelectedTemplateData(null);
    setIsComposeOpen(false);
    fetchEmails();
    setSelectedOption("");
  };

  const handleDeleteEmail = (email: any) => {
    setEmailToDelete(email);
    setIsArchivePopupOpen(true);
  };

  const confirmDeleteEmail = async () => {
    if (!emailToDelete) return;
    setLoading(true);
    try {
      await deleteEmail(emailToDelete.id);
      setAlertMessage("Email archived successfully.");
      setAlertType("success");
      setAlertOpen(true);
      fetchEmails();
    } catch (error: any) {
      setAlertMessage("Failed to archive Email.");
      setAlertType("error");
      setAlertOpen(true);
    } finally {
      setIsArchivePopupOpen(false);
      setEmailToDelete(null);
      setLoading(false);
    }
  };

  return (
    <div className="campaign-email-info">
      <div className="campaign-email-info-header">
        <div className="campaign-email-info-header-text">Email</div>
        <div className="select-template-option-dropdown-wrapper">
          <SelectTemplateOption
            selectedOption={selectedOption}
            handleChange={handleOptionChange}
            disabled={!isEmailConfigured || !permissions?.canWrite}
            placeholder={`Compose Email`}
            options={emailComposeOptions}
          />
        </div>
      </div>
      {!isEmailConfigured && (
        <div className="email-setup-banner-wrapper">
          <EmailSetUpBanner />
        </div>
      )}
      {emailsData && emailsData.length > 0 ? (
        <div className="campaign-email-table">
          <DataTable
            columns={campaignEmailColumns}
            data={emailsData}
            rowActions={(email) => (
              <div style={{ display: "flex" }}>
                <Tooltip className="tooltip" title="Archive">
                  <IconButton
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteEmail(email);
                    }}
                    className="icon-button"
                    disabled={!permissions?.canDelete}
                  >
                    <img src={DeleteIcon} alt="archive" className="img-icon" />
                  </IconButton>
                </Tooltip>
              </div>
            )}
          />
        </div>
      ) : (
        <div className="campaign-email-info-no-data">No records found.</div>
      )}

      <ConfirmationPopup
        open={isArchivePopupOpen}
        onCancel={() => {
          setIsArchivePopupOpen(false);
          setEmailToDelete(null);
        }}
        onOkay={confirmDeleteEmail}
        title={`Archive  Email`}
        note={
          emailToDelete
            ? "Are you sure you want to archive the Email?"
            : "Are you sure you want to archive this Email?"
        }
        buttonText="Archive"
      />

      <ComposeEmailDialog
        open={isComposeOpen}
        onClose={() => {
          setSelectedTemplateData(null);
          setIsComposeOpen(false);
          setSelectedOption("");
        }}
        // leadDetails={leadDetails}
        campaignDetails={campaignDetails}
        onSuccess={handleEmailSuccess}
        templateData={selectedTemplateData}
        templateMetaData={templateMetaData}
        setSelectedTemplateData={setSelectedTemplateData}
      />
      <TemplateListPopup
        open={isTemplateListOpen}
        onClose={() => {
          setIsTemplateListOpen(false);
          setSelectedOption("");
          setSelectedTemplateData(null);
        }}
        title={`Existing Email Templates`}
        templates={templates}
        selectedTemplateData={selectedTemplateData}
        setSelectedTemplateData={setSelectedTemplateData}
        onSelect={() => {
          handleTemplateSelect();
        }}
      />
      <Dialog open={isRecipientsOpen} maxWidth="md" fullWidth>
        <DialogTitle>
          Recipients
          <IconButton
            aria-label="close"
            onClick={() => setIsRecipientsOpen(false)}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {recipientData.length > 0 ? (
            <DataTable columns={recipentColumns} data={recipientData} />
          ) : (
            <div className="campaign-email-info-no-data">No records found.</div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CampaignEmailInfo;
